import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Download, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "border-brand/40 bg-brand/10 text-red-400",
  reviewing: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  contacted: "border-sky-500/40 bg-sky-500/10 text-sky-400",
  closed: "border-zinc-500/40 bg-zinc-500/10 text-zinc-400",
};

const STATUSES = ["new", "reviewing", "contacted", "closed"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const authed = async (fn) => {
    try {
      return await fn();
    } catch (e) {
      if (e.response?.status === 401) {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return await fn();
      }
      throw e;
    }
  };

  const load = async () => {
    const res = await authed(() => axios.get(`${API}/admin/enquiries`, { withCredentials: true }));
    setEnquiries(res.data);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (user) load().catch(() => setUser(false));
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
    setEnquiries([]);
  };

  const removeEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await authed(() => axios.delete(`${API}/admin/enquiries/${id}`, { withCredentials: true }));
      setEnquiries((list) => list.filter((enq) => enq.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  const exportCsv = () => {
    const cols = ["created_at", "name", "phone", "email", "company", "org_type", "party", "state", "service", "budget", "status", "message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...filtered.map((e) => cols.map((c) => esc(e[c])).join(","))];
    const blob = new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `onward-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${filtered.length} enquiries`);
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await authed(() =>
        axios.patch(`${API}/admin/enquiries/${id}`, { status }, { withCredentials: true })
      );
      setEnquiries((list) => list.map((enq) => (enq.id === id ? res.data : enq)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</p>
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6" data-testid="admin-login-page">
        <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-coal p-8">
          <Logo size="md" testId="admin-login-logo" />
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Admin sign in</p>
          <form onSubmit={login} className="mt-6 space-y-4" data-testid="admin-login-form">
            <Input
              data-testid="admin-email-input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-white/15 bg-ink text-paper placeholder:text-zinc-600 focus-visible:ring-brand"
            />
            <Input
              data-testid="admin-password-input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border-white/15 bg-ink text-paper placeholder:text-zinc-600 focus-visible:ring-brand"
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="btn-ribbon w-full rounded-full py-3 font-mono text-xs font-medium uppercase tracking-[0.15em] text-white disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const filtered = enquiries.filter((enq) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q || [enq.name, enq.email, enq.phone, enq.company, enq.service, enq.state, enq.party].filter(Boolean).some((v) => v.toLowerCase().includes(q));
    const matchesStatus = statusFilter === "all" || enq.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const metrics = [
    { label: "Total enquiries", value: enquiries.length, testId: "metric-total" },
    { label: "New", value: enquiries.filter((e) => e.status === "new").length, testId: "metric-new" },
    { label: "In progress", value: enquiries.filter((e) => e.status === "reviewing" || e.status === "contacted").length, testId: "metric-in-progress" },
    { label: "Closed", value: enquiries.filter((e) => e.status === "closed").length, testId: "metric-closed" },
  ];

  return (
    <div className="min-h-screen bg-ink text-paper" data-testid="admin-dashboard">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 lg:px-12">
          <div className="flex items-center gap-4">
            <Logo size="sm" testId="admin-logo" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Enquiries</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-xs text-zinc-500 sm:block" data-testid="admin-user-email">{user.email}</span>
            <button
              data-testid="admin-export-csv"
              onClick={exportCsv}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 transition-colors duration-300 hover:border-saffron hover:text-saffron"
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 transition-colors duration-300 hover:border-brand hover:text-brand"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1440px] px-6 py-10 lg:px-12">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {metrics.map((m) => (
            <div key={m.label} data-testid={m.testId} className="rounded-xl border border-white/10 bg-coal p-6">
              <p className="font-display text-3xl font-bold text-paper">{m.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Input
            data-testid="admin-search-input"
            placeholder="Search name, phone, email, organisation, service…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm border-white/15 bg-coal text-paper placeholder:text-zinc-600 focus-visible:ring-brand"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="admin-status-filter" className="w-[160px] border-white/15 bg-coal text-paper focus:ring-brand">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-coal text-paper">
              <SelectItem value="all" data-testid="status-filter-all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`status-filter-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            data-testid="admin-refresh-button"
            onClick={() => load().catch(() => {})}
            className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 transition-colors duration-300 hover:border-brand hover:text-brand"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-white/10" data-testid="enquiries-table">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-coal">
                {["Date", "Contact", "Type / State", "Service", "Budget", "Message", "Status", ""].map((h) => (
                  <th key={h} className="px-5 py-4 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-zinc-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((enq) => (
                <tr key={enq.id} data-testid={`enquiry-row-${enq.id}`} className="border-b border-white/5 transition-colors hover:bg-white/[0.02]">
                  <td className="whitespace-nowrap px-5 py-4 font-mono text-xs text-zinc-500">
                    {new Date(enq.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-medium text-paper">{enq.name}</p>
                    <p className="text-xs text-zinc-500">{enq.email}{enq.company ? ` — ${enq.company}` : ""}</p>
                    {enq.phone && (
                      <a href={`https://wa.me/${enq.phone.replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}`} target="_blank" rel="noreferrer" data-testid={`enquiry-phone-${enq.id}`} className="mt-1 inline-block font-mono text-xs text-saffron hover:underline">
                        {enq.phone}
                      </a>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-5 py-4 text-xs text-zinc-400">
                    <p>{enq.org_type || "—"}{enq.party ? ` · ${enq.party}` : ""}</p>
                    <p className="text-zinc-500">{enq.state || ""}</p>
                  </td>
                  <td className="px-5 py-4 text-zinc-300">{enq.service}</td>
                  <td className="whitespace-nowrap px-5 py-4 text-zinc-400">{enq.budget || "—"}</td>
                  <td className="max-w-[260px] px-5 py-4">
                    <p className="truncate text-zinc-400" title={enq.message}>{enq.message}</p>
                  </td>
                  <td className="px-5 py-4">
                    <Select value={enq.status} onValueChange={(v) => updateStatus(enq.id, v)}>
                      <SelectTrigger
                        data-testid={`status-select-${enq.id}`}
                        className={`h-8 w-[130px] rounded-full border px-3 font-mono text-[10px] uppercase tracking-[0.12em] ${STATUS_STYLES[enq.status]}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-coal text-paper">
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`status-option-${enq.id}-${s}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-4">
                    <button
                      type="button"
                      aria-label="Delete enquiry"
                      data-testid={`delete-enquiry-${enq.id}`}
                      onClick={() => removeEnquiry(enq.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-zinc-500 transition-colors duration-300 hover:border-brand hover:text-brand"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-16 text-center" data-testid="enquiries-empty">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-600">No enquiries yet</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
